import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowRight, Mic, Stethoscope, ClipboardList, FileText, Mail, TrendingUp,
  ShieldCheck, ClipboardCheck, Users, FolderOpen, Layers, GraduationCap, Sparkles,
  FolderKanban,
} from "lucide-react";

/**
 * Paradygma-inspiriertes Dashboard — DH-Farben, Mobile-first.
 * Zeigt eine kategorisierte Karten-Übersicht statt 13 Tabs. Jede Karte
 * wechselt zum entsprechenden bestehenden Tab.
 */

const SECTIONS = [
  {
    id: "alltag",
    title: "Alltag & Doku",
    subtitle: "Was du täglich brauchst",
    cards: [
      { tab: "ampel", chip: "AM", label: "Pflegeampel",
        icon: Sparkles, desc: "Beobachtung eintragen — sofort Grün/Gelb/Rot mit Sofortmaßnahmen und Meldung.", accent: "bordeaux" },
      { tab: "dokukette", chip: "DK", label: "Dokukette",
        icon: ClipboardList, desc: "In einem Fluss von Beobachtung → Risiko → Maßnahme → Kommunikation → Evaluation.", accent: "terra" },
      { tab: "aufnahme", chip: "AU", label: "Aufnahmegespräch",
        icon: FileText, desc: "6-Sektionen-Formular mit Unterschrift-Pad. Am Ende: komplette Aufnahme-Dokukette.", accent: "bordeaux" },
      { tab: "interview", chip: "IV", label: "Interview-Modus",
        icon: Mic, desc: "Sprich mit der KI wie mit einer Kollegin – sie füllt SIS & Bericht mit dir.", accent: "terra" },
      { tab: "eingabe", chip: "PA", label: "Patient anlegen / Eingabe",
        icon: Stethoscope, desc: "Stammdaten, Pflegesituation und Risiken für einen neuen Fall erfassen.", accent: "sand" },
      { tab: "sis", chip: "SI", label: "SIS erstellen",
        icon: ClipboardList, desc: "Strukturierte Informations­sammlung A1–A6, sauber und prüffähig.", accent: "bordeaux" },
      { tab: "massnahmen", chip: "MP", label: "Maßnahmenplan",
        icon: FileText, desc: "7-Spalten-Plan (Problem · Ziel · Maßnahme · Häufigkeit · …) auf Basis der SIS.", accent: "bordeaux" },
      { tab: "pflegebericht", chip: "PB", label: "Pflegebericht per Diktat",
        icon: Mic, desc: "Stichpunkte einsprechen – die KI schreibt den sachlichen Bericht.", accent: "terra" },
    ],
  },
  {
    id: "abrechnung",
    title: "Abrechnung & Widerspruch",
    subtitle: "Wenn es um Geld und Gutachten geht",
    cards: [
      { tab: "kassenbrief", chip: "KB", label: "Kassenbrief",
        icon: Mail, desc: "Formgerechte Briefe an Pflege- und Krankenkassen – aus dem Fall generiert.", accent: "bordeaux" },
      { tab: "hoehergruppe", chip: "HS", label: "Höherstufung",
        icon: TrendingUp, desc: "Antrags- und Widerspruchs-Entwurf nach NBA-Logik – begründet und schlüssig.", accent: "bordeaux" },
      { tab: "nba", chip: "NB", label: "NBA-Analyse",
        icon: ShieldCheck, desc: "Modul-Punkte für die 6 NBA-Bereiche schätzen und Pflegegrad ableiten.", accent: "sand" },
      { tab: "qualitaet", chip: "QC", label: "Qualitätscheck",
        icon: ClipboardCheck, desc: "SIS, Plan und Brief gegenprüfen – fehlende Ziele, Widersprüche, Sprache.", accent: "terra" },
    ],
  },
  {
    id: "praxis",
    title: "Praxis, Team & Prüfung",
    subtitle: "Für PDL und Weiterbildung",
    cards: [
      { tab: "klientenakte", chip: "360", label: "360°-Klientenakte",
        icon: FolderOpen, desc: "Aufnahmen · Ampel-Verläufe · Dokuketten pro Bewohner chronologisch versammelt.", accent: "bordeaux" },
      { tab: "kundenakte", chip: "KA", label: "Kundenakte (klassisch)",
        icon: FolderOpen, desc: "Verlauf und Historie pro Bewohner – alle Entwürfe an einem Ort.", accent: "sand" },
      { tab: "sammel", chip: "SA", label: "Sammel-Audit",
        icon: Layers, desc: "Mehrere Fälle in einem Rutsch prüfen – ideal für Team- und QM-Audits.", accent: "sand" },
      { tab: "md", chip: "MD", label: "MD-Prüfungs-Simulator",
        icon: GraduationCap, desc: "Simuliere eine MD-Qualitätsprüfung mit deinen echten Dokumenten.", accent: "terra" },
      { tab: "entwuerfe", chip: "EN", label: "Meine Entwürfe",
        icon: Users, desc: "Alle deine Entwürfe – laden, weiterarbeiten oder als PDF exportieren.", accent: "sand" },
    ],
  },
];

const ACCENT_STYLES = {
  bordeaux: "bg-brand-darkred text-cream-50",
  terra:    "bg-terra text-white",
  sand:     "bg-sand-800 text-cream-50",
};

export default function PkiDashboard({ userLabel = "", onOpenTab }) {
  const [query, setQuery] = useState("");

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map((s) => ({
      ...s,
      cards: s.cards.filter(
        (c) => c.label.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q) || c.chip.toLowerCase().includes(q),
      ),
    })).filter((s) => s.cards.length > 0);
  }, [query]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 11) return "Guten Morgen";
    if (h < 18) return "Guten Tag";
    return "Guten Abend";
  }, []);

  return (
    <div data-testid="pki-dashboard" className="space-y-8">
      {/* HERO — freundliche Begrüßung + Command-Bar */}
      <div className="bg-gradient-to-br from-sand-800 via-brand-darkred to-terra rounded-3xl p-6 sm:p-8 md:p-10 text-cream-50 shadow-soft">
        <div className="flex items-center gap-2 text-cream-100/85 text-[12px] font-semibold uppercase tracking-[0.22em]">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} /> Pflege-KI Assistenz
        </div>
        <h1 data-testid="pki-dashboard-greeting"
          className="mt-3 font-display text-[26px] sm:text-3xl md:text-4xl leading-tight">
          {greeting}{userLabel ? `, ${userLabel}` : ""}. Womit möchtest du beginnen?
        </h1>
        <p className="mt-3 text-cream-100/85 text-[14px] leading-relaxed max-w-2xl">
          Ein Werkzeug – ein Ziel: aus deinem Fall entsteht in wenigen Minuten
          eine prüffähige, saubere Doku. Wähle unten den nächsten Schritt.
        </p>

        {/* Command-Bar */}
        <div className="mt-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-cream-100/70" strokeWidth={1.7} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="pki-dashboard-search"
            placeholder={`Suche z. B. \u201eKassenbrief\u201c, \u201eSIS\u201c, \u201eDiktat\u201c\u2026`}
            className="w-full pl-12 pr-4 h-12 rounded-2xl bg-cream-50/10 border border-cream-50/20 text-cream-50 placeholder:text-cream-100/60 focus:outline-none focus:bg-cream-50/15 focus:border-cream-50/40 text-[14.5px]"
          />
        </div>
      </div>

      {/* Patientenmappe-Hero (Paradygma-Suite) */}
      <Link to="/campus/app/patientenmappe" data-testid="pki-dashboard-mappe-banner"
        className="block relative overflow-hidden rounded-3xl border-2 border-brand-darkred/30 bg-white hover:border-brand-darkred hover:shadow-hover transition-all group">
        <div className="absolute -right-10 -top-10 opacity-5 pointer-events-none">
          <FolderKanban className="h-56 w-56 text-brand-darkred" strokeWidth={0.6} />
        </div>
        <div className="relative p-5 sm:p-6 md:p-7 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="inline-flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-darkred text-cream-50">
            <FolderKanban className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron/30 text-brand-darkred px-2 py-0.5 text-[10px] font-bold tracking-widest mb-1">
              <Sparkles className="h-3 w-3" /> NEU · PARADYGMA-SUITE
            </div>
            <div className="font-display text-2xl sm:text-3xl text-sand-800 leading-tight">Patientenmappen · 11 Sektionen</div>
            <p className="text-[13.5px] text-sand-600 mt-1 leading-relaxed max-w-2xl">
              SIS · Risikomatrix · Medikation · Maßnahmenplan · 360°-Beobachtung · QPR-26 · Freigaben mit Signatur —
              Cloud-basiert & Team-fähig. <strong className="text-brand-darkred">Beispiel-Mappe klickbar.</strong>
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-4 py-2 text-[13.5px] font-semibold group-hover:bg-brand-red transition-colors shrink-0 self-start sm:self-center">
            Öffnen <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>

      {/* SEKTIONEN */}
      {sections.map((section) => (
        <div key={section.id} data-testid={`pki-section-${section.id}`}>
          <div className="mb-4">
            <h2 className="font-display text-xl sm:text-2xl text-sand-800 leading-tight">
              {section.title}
            </h2>
            <p className="text-[13px] text-sand-600 mt-1">{section.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {section.cards.map(({ tab, chip, label, icon: Icon, desc, accent }) => (
              <button
                key={tab}
                type="button"
                onClick={() => onOpenTab?.(tab)}
                data-testid={`pki-card-${tab}`}
                className="group text-left bg-white border border-cream-300 rounded-2xl p-4 sm:p-5 min-h-[140px] flex flex-col justify-between transition-all hover:border-brand-darkred/40 hover:shadow-hover active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-brand-darkred/30"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`inline-flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-xl font-bold text-[14px] sm:text-[15px] tracking-[0.02em] ${ACCENT_STYLES[accent] || ACCENT_STYLES.sand}`}
                  >
                    {chip}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sand-800 text-[15px] sm:text-[16px] leading-snug flex items-start justify-between gap-2">
                      <span className="min-w-0">{label}</span>
                      <Icon className="h-4 w-4 text-sand-500 group-hover:text-brand-darkred shrink-0 mt-1 transition-colors" strokeWidth={1.7} />
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-[13px] sm:text-[13.5px] text-sand-600 leading-relaxed">
                  {desc}
                </p>
                <div className="mt-3 pt-3 border-t border-cream-200 flex items-center justify-between text-[12.5px] font-semibold text-brand-darkred">
                  Öffnen
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Fallback bei leerer Suche */}
      {sections.length === 0 && (
        <div data-testid="pki-dashboard-empty" className="text-center py-16 text-sand-600 text-[14px]">
          Keine Werkzeuge für {'\u201e'}{query}{'\u201c'} gefunden.
          <br />
          <button className="mt-3 text-brand-darkred underline underline-offset-4" onClick={() => setQuery("")}>
            Suche zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
}
